/**
 * useWipComparison
 *
 * WIP is frontend-only: no backend aggregates this data, since no single
 * backend owns both bookings and time entries. This fetches SSS's
 * schedule bookings (GET /schedules) and TRS's time entries
 * (GET /post-times, via the shared useTrsTimeEntries.js) as two separate
 * REST calls, then computes every variance number CLIENT-SIDE.
 *
 * Only CHARGEABLE CLIENT time entries count toward actual hours here
 * (`isChargeableClientEntry()` -- job_type === 'client' with a real
 * engagement) -- office/expense entries are real TRS data but aren't
 * engagement-attributable client work, so including them would inflate
 * every engagement's actual hours with unrelated internal time. This
 * wasn't filtered before TRS's real `job_type` field was confirmed.
 *
 * ⚠️ `/schedules`' `engagement_id` param and `/post-times`' full query
 * contract are still best-guess -- see useReferenceData.js/
 * useTrsTimeEntries.js's docblocks for what's confirmed vs. not. If a
 * fetch comes back empty or the numbers look wrong, those files
 * (specifically the query params, not the field readers below, which
 * are now grounded in TRS's confirmed real model) are the first place
 * to check against what the network tab actually shows.
 *
 * Business rules (unchanged from the original spec):
 *   hourVariance = actualHours - scheduledHours (positive = over-budget)
 *   costVariance = hourVariance x the staff's position's current hourly rate
 *   status: unscheduled (actual only) / no_show (scheduled only) /
 *           over_budget (actual > scheduled x 1.10) / on_track
 */
import { useMemo } from 'react';
import { useEngagements, useClients, useStaffList, usePositionRates, useSchedules as useSssSchedules } from './useReferenceData.js';
import {
  useTimeEntries as useTrsTimeEntries,
  timeEntryUserId,
  timeEntryEngagementId,
  timeEntryHours,
  timeEntryStaffName,
  isChargeableClientEntry,
} from './useTrsTimeEntries.js';

/** @typedef {'cutoff_1'|'cutoff_2'} CutOffPeriod */

/**
 * @typedef {object} WipFilters
 * @property {string|null} branchId
 * @property {CutOffPeriod} cutOffPeriod
 * @property {string[]} staffIds
 * @property {string|null} engagementId
 * @property {number} page
 * @property {number} perPage
 */

/**
 * @typedef {object} ComparisonRow
 * @property {string} id
 * @property {string} staffName
 * @property {string} position
 * @property {string} clientName
 * @property {string} engagementId
 * @property {string} engagementName
 * @property {number} scheduledHours
 * @property {number} actualHours
 * @property {number} hourVariance
 * @property {number} costVariance
 * @property {'on_track'|'over_budget'|'unscheduled'|'no_show'} status
 */

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Resolves a cut-off period to a concrete date range within the CURRENT month. */
function cutOffDateRange(period) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed

  if (period === 'cutoff_1') {
    return [new Date(year, month, 1), new Date(year, month, 15)];
  }
  return [new Date(year, month, 16), new Date(year, month + 1, 0)];
}

const toDateParam = (d) => d.toISOString().slice(0, 10);

/**
 * Thin wrapper around the shared useReferenceData.js useSchedules,
 * converting this file's cut-off-period filter shape into concrete
 * date_from/date_to params.
 * @param {WipFilters} filters
 */
function useSchedules(filters) {
  const [start, end] = cutOffDateRange(filters.cutOffPeriod);
  return useSssSchedules({
    branchId: filters.branchId,
    engagementId: filters.engagementId,
    dateFrom: toDateParam(start),
    dateTo: toDateParam(end),
  });
}

/** @param {WipFilters} filters */
function useTimeEntries(filters) {
  const [start, end] = cutOffDateRange(filters.cutOffPeriod);
  return useTrsTimeEntries({
    engagementId: filters.engagementId,
    dateFrom: toDateParam(start),
    dateTo: toDateParam(end),
  });
}

/**
 * Pure function: one row per (engagement, staff) pair that has EITHER a
 * schedule booking OR a chargeable client time entry (or both) in the
 * fetched data. Exported for easy unit testing independent of React Query.
 * @returns {ComparisonRow[]}
 */
export function computeComparisonRows({ schedules, timeEntries, engagements, clients, staff, positionRates, filters }) {
  const engagementById = new Map(engagements.map((e) => [String(e.id), e]));
  const clientById = new Map(clients.map((c) => [String(c.id), c]));
  const staffById = new Map(staff.map((s) => [String(s.id ?? s.user_id), s]));

  const latestRateByPosition = new Map();
  [...positionRates]
    .sort((a, b) => new Date(a.effective_date) - new Date(b.effective_date))
    .forEach((r) => latestRateByPosition.set(r.position_title, Number(r.hourly_rate)));

  const scheduledByKey = new Map();
  for (const s of schedules) {
    if (!s.engagement_id) continue;
    const key = `${s.engagement_id}:${s.staff_id ?? s.user_id}`;
    const hours = Number(s.hours ?? s.scheduled_hours ?? 0);
    scheduledByKey.set(key, (scheduledByKey.get(key) ?? 0) + hours);
  }

  const actualByKey = new Map();
  for (const t of timeEntries) {
    if (!isChargeableClientEntry(t)) continue;
    const key = `${timeEntryEngagementId(t)}:${timeEntryUserId(t)}`;
    actualByKey.set(key, (actualByKey.get(key) ?? 0) + timeEntryHours(t));
  }

  const keys = new Set([...scheduledByKey.keys(), ...actualByKey.keys()]);
  const rows = [];

  for (const key of keys) {
    const [engagementId, staffId] = key.split(':');

    if (filters.engagementId && engagementId !== String(filters.engagementId)) continue;
    if (filters.staffIds?.length && !filters.staffIds.map(String).includes(staffId)) continue;

    const scheduledHours = scheduledByKey.get(key) ?? 0;
    const actualHours = actualByKey.get(key) ?? 0;
    const hourVariance = actualHours - scheduledHours;

    const engagement = engagementById.get(engagementId);
    const staffRow = staffById.get(staffId);
    const position = staffRow?.position ?? null;
    const rate = position ? (latestRateByPosition.get(position) ?? 0) : 0;

    let status = 'on_track';
    if (scheduledHours === 0 && actualHours > 0) status = 'unscheduled';
    else if (scheduledHours > 0 && actualHours === 0) status = 'no_show';
    else if (scheduledHours > 0 && actualHours > scheduledHours * 1.1) status = 'over_budget';

    rows.push({
      id: key,
      staffName: staffRow?.display_name ?? staffRow?.full_name ?? staffRow?.name ?? `Staff #${staffId}`,
      position: position ?? 'Unassigned',
      clientName: clientById.get(String(engagement?.client_id))?.name ?? 'Unknown Client',
      engagementId,
      engagementName: engagement?.title ?? engagement?.name ?? `Engagement #${engagementId}`,
      scheduledHours: round2(scheduledHours),
      actualHours: round2(actualHours),
      hourVariance: round2(hourVariance),
      costVariance: round2(hourVariance * rate),
      status,
    });
  }

  return rows;
}

/**
 * Fetches everything needed (schedules, time entries, engagements,
 * clients, staff, position rates) and computes the comparison rows.
 *
 * `isError`/`isLoading` reflect only the SSS-driven data (schedules +
 * reference data) -- a TRS-specific failure is tracked separately via
 * `isTrsError` and does NOT blank out real SSS-driven rows; those rows
 * just end up with `actualHours: 0` for everything, same graceful
 * degradation the other pages use. Callers should only fall back to
 * sample data on `isError` (can't build any real rows at all) or
 * `isTrsError` alongside zero rows, NOT merely because `rows` came back
 * empty from a legitimately narrow filter -- a genuinely empty cut-off
 * period is real information, not something to hide behind fictional
 * sample numbers.
 * @param {WipFilters} filters
 */
export function useComparisonRows(filters) {
  const schedulesQuery = useSchedules(filters);
  const timeEntriesQuery = useTimeEntries(filters);
  const engagementsQuery = useEngagements(filters.branchId ? { branch_id: filters.branchId } : {});
  const clientsQuery = useClients();
  const staffQuery = useStaffList(filters.branchId ? { branch_id: filters.branchId } : {});
  const positionRatesQuery = usePositionRates();

  const coreQueries = [schedulesQuery, engagementsQuery, clientsQuery, staffQuery, positionRatesQuery];
  const isLoading = coreQueries.some((q) => q.isLoading) || timeEntriesQuery.isLoading;
  const isError = coreQueries.some((q) => q.isError);
  const isTrsError = timeEntriesQuery.isError;

  const rows = useMemo(() => {
    if (isLoading || isError) return [];
    return computeComparisonRows({
      schedules: schedulesQuery.data ?? [],
      // If TRS specifically failed, compute with zero time entries
      // rather than blanking out the whole result -- real scheduled
      // hours still show, actualHours just reads 0 for everything.
      timeEntries: isTrsError ? [] : (timeEntriesQuery.data ?? []),
      engagements: engagementsQuery.data ?? [],
      clients: clientsQuery.data ?? [],
      staff: staffQuery.data ?? [],
      positionRates: positionRatesQuery.data ?? [],
      filters,
    });
  }, [
    isLoading, isError, isTrsError, schedulesQuery.data, timeEntriesQuery.data, engagementsQuery.data,
    clientsQuery.data, staffQuery.data, positionRatesQuery.data, filters,
  ]);

  return { rows, isLoading, isError, isTrsError };
}

/** @param {ComparisonRow[]} rows */
export function summarizeRows(rows) {
  const totalScheduled = round2(rows.reduce((sum, r) => sum + r.scheduledHours, 0));
  const totalActual = round2(rows.reduce((sum, r) => sum + r.actualHours, 0));

  return {
    total_scheduled_hours: totalScheduled,
    total_actual_hours: totalActual,
    net_hour_variance: round2(totalActual - totalScheduled),
    total_wip_cost_variance: round2(rows.reduce((sum, r) => sum + r.costVariance, 0)),
    unscheduled_actuals_count: rows.filter((r) => r.status === 'unscheduled').length,
  };
}

/**
 * Synthesizes audit-trail entries from schedules'/time entries'
 * created_at/updated_at timestamps for one engagement -- there's no
 * dedicated audit-log table on either backend, so this is a best-effort
 * reconstruction rather than a true change log.
 * @param {string|null} engagementId
 */
export function useAuditHistory(engagementId) {
  const schedulesQuery = useSssSchedules({ engagementId, enabled: Boolean(engagementId) });
  const timeEntriesQuery = useTrsTimeEntries({ engagementId, enabled: Boolean(engagementId) });

  const entries = useMemo(() => {
    const fromSchedules = (schedulesQuery.data ?? []).flatMap((s) => {
      const actor = s.staff_name ?? `Staff #${s.staff_id ?? s.user_id}`;
      const hours = s.hours ?? s.scheduled_hours ?? 0;
      const created = s.created_at
        ? [{ id: `sss-${s.id}-created`, source: 'sss', description: `Booking created: ${hours} hrs scheduled`, actor, occurredAt: s.created_at }]
        : [];
      const updated = s.updated_at && s.updated_at !== s.created_at
        ? [{ id: `sss-${s.id}-updated`, source: 'sss', description: `Booking updated: now ${hours} hrs scheduled`, actor, occurredAt: s.updated_at }]
        : [];
      return [...created, ...updated];
    });

    const fromTimeEntries = (timeEntriesQuery.data ?? [])
      .filter(isChargeableClientEntry)
      .map((t) => ({
        id: `trs-${t.id}`,
        source: 'trs',
        description: `Time entry submitted: ${timeEntryHours(t)} hrs on ${t.date ?? ''}`,
        actor: timeEntryStaffName(t) ?? `Staff #${timeEntryUserId(t)}`,
        occurredAt: t.created_at,
      }));

    return [...fromSchedules, ...fromTimeEntries]
      .filter((e) => e.occurredAt)
      .sort((a, b) => new Date(b.occurredAt) - new Date(a.occurredAt));
  }, [schedulesQuery.data, timeEntriesQuery.data]);

  return {
    data: { entries },
    isLoading: schedulesQuery.isLoading || timeEntriesQuery.isLoading,
  };
}

function csvEscape(value) {
  const str = String(value ?? '');
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

/**
 * Client-side CSV export -- there's no backend export endpoint anymore
 * (no backend owns the joined data), so this builds and downloads the
 * file directly in the browser from whatever rows are currently computed.
 * @param {ComparisonRow[]} rows
 * @param {CutOffPeriod} cutOffPeriod
 */
export function exportComparisonRowsToCsv(rows, cutOffPeriod) {
  const header = [
    'Staff Name', 'Position', 'Client', 'Engagement',
    'Scheduled Hours', 'Actual Hours', 'Variance (Hrs)', 'WIP Cost Variance', 'Status',
  ];
  const lines = [
    header,
    ...rows.map((r) => [
      r.staffName, r.position, r.clientName, r.engagementName,
      r.scheduledHours, r.actualHours, r.hourVariance, r.costVariance, r.status,
    ]),
  ];
  const csv = lines.map((line) => line.map(csvEscape).join(',')).join('\r\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `wip-variance-${cutOffPeriod}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
